import { View, Flex, Heading, Text, Button, Loader, Alert } from "@aws-amplify/ui-react";
import { useState, useEffect } from "react";
import { API, Auth } from "aws-amplify";
import { useNavigate, useParams } from "react-router-dom";
import { validateListing, slugify, publicListingUrl, emptyListing, toFormValues } from "./listingModel";
import { BasicFields, MediaFields } from "./ListingFieldsBasic";
import { DealFields, ContactFields } from "./ListingFieldsDeal";

// Container for /listings/new and /listings/edit/:slug (docs/listings.md section 5.1).
// Identity (userId/email) is taken from the Cognito token server-side; the body copies are ignored there.
async function authRequest(body) {
    const user = await Auth.currentAuthenticatedUser();
    const request = { headers: { Authorization: `${user.signInUserSession.idToken.jwtToken}` } };
    if (body) request.body = body;
    return request;
}

export function ListingForm({ mode, clientData }) {
    const { slug } = useParams();
    const navigate = useNavigate();
    const isEdit = mode === "edit";
    const email = clientData && clientData.email;
    const [values, setValues] = useState(emptyListing);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [submitError, setSubmitError] = useState("");

    useEffect(() => {
        if (!isEdit) return;
        let cancelled = false;
        async function load() {
            try {
                const listings = await API.get("gcAPI", "/listings", await authRequest());
                const found = Array.isArray(listings) && listings.find((l) => l.slug === slug);
                if (cancelled) return;
                if (found) setValues(toFormValues(found));
                else setSubmitError("Listing not found");
            } catch (error) {
                console.error("Error loading listing", error);
                if (!cancelled) setSubmitError("Could not load listing, try again");
            }
            if (!cancelled) setLoading(false);
        }
        load();
        return () => { cancelled = true; };
    }, [isEdit, slug]);

    // feeType and feeAmount validate as a pair (one without the other is an error), so editing
    // either must clear both messages — otherwise clearing the fee type leaves a stale
    // "amount is required" under a field whose precondition is gone.
    const onChange = (field, value) => {
        setValues((prev) => ({ ...prev, [field]: value }));
        const paired = field === "feeType" || field === "feeAmount" ? ["feeType", "feeAmount"] : [field];
        setErrors((prev) => {
            if (!paired.some((f) => prev[f])) return prev;
            const next = { ...prev };
            for (const f of paired) next[f] = undefined;
            return next;
        });
    };

    async function handleSave() {
        setSubmitError("");
        const { errors: fieldErrors, value } = validateListing(values);
        if (Object.keys(fieldErrors).length) {
            setErrors(fieldErrors);
            window.scrollTo(0, 0);
            return;
        }
        setSaving(true);
        try {
            const body = { action: isEdit ? "update" : "create", ...value };
            if (isEdit) body.slug = slug;
            const resp = await API.post("gcAPI", "/listings", await authRequest(body));
            navigate("/listings", { state: { saved: resp && resp.listing } });
        } catch (err) {
            const data = err.response && err.response.data;
            if (data && data.errors) setErrors(data.errors);
            else setSubmitError(data && data.error ? `Could not save (${data.error})` : "Could not save, try again");
            window.scrollTo(0, 0);
            setSaving(false);
        }
    }

    const previewSlug = isEdit ? slug : (values.title.trim() ? slugify(values.title) : "…");
    const showPreview = values.visibility === "public";

    return (
        <View align="center">
            <View maxWidth="800px" align="left">
                <Heading level={3} marginBottom="15px">{isEdit ? "Edit listing" : "New listing"}</Heading>
                {submitError && <Alert variation="error" marginBottom="15px">{submitError}</Alert>}
                {loading ? <Loader /> : (
                    <Flex direction="column" gap="20px">
                        {showPreview && (
                            <Text fontSize="small" color="font.tertiary">
                                Public URL: {publicListingUrl(previewSlug)}
                                {!isEdit && " (final URL may get a numeric suffix if the title is already taken)"}
                            </Text>
                        )}
                        <BasicFields values={values} errors={errors} onChange={onChange} isEdit={isEdit} />
                        <MediaFields values={values} errors={errors} onChange={onChange} />
                        <DealFields values={values} errors={errors} onChange={onChange} />
                        <ContactFields values={values} errors={errors} onChange={onChange} accountEmail={email} />
                        <Flex direction="row" gap="10px">
                            <Button variation="primary" isLoading={saving} onClick={handleSave}>Save</Button>
                            <Button onClick={() => navigate("/listings")} isDisabled={saving}>Cancel</Button>
                        </Flex>
                    </Flex>
                )}
            </View>
        </View>
    );
}
